/**
 * Audit Log Details Drawer
 *
 * Right-side drawer (shared AppDrawer shell) opened by the table's "View"
 * action. Friendly Summary first (who / what / what was affected), technical
 * context next, raw JSON demoted to collapsed accordions at the bottom.
 */

import React from 'react'
import {
  Drawer,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  AppDrawerShell,
  AppDrawerHeader,
  AppDrawerBody,
  APP_DRAWER_WIDTH,
  APP_DRAWER_BG,
  APP_DRAWER_SHADOW,
} from '../../../../components/shared/AppDrawer'
import { AuditLog } from '../../../../services/auditApi'
import { formatDateTime } from '../../../../utils/inspectorFormatters'
import {
  humaniseEventType,
  formatCategory,
  resultLabel,
  severityColor,
  SEVERITY_LABELS,
} from '../auditLabels'

interface AuditLogDrawerProps {
  open: boolean
  onClose: () => void
  log: AuditLog | null
  /** value -> label map from the /event-types payload (optional). */
  labelMap?: Map<string, string>
}

/** One labelled summary row; renders nothing when value is empty. */
const SummaryRow: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => {
  if (children === null || children === undefined || children === '') return null
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component="div">{children}</Typography>
    </Grid>
  )
}

// Known affected-count keys we surface in the Summary (others stay in Raw).
const COUNT_KEYS: Array<[string, string]> = [
  ['count', 'Affected'],
  ['skipped_count', 'Skipped'],
  ['errors', 'Errors'],
  ['total_records_cleared', 'Records cleared'],
]

const outcomeIcon = (success: string) => {
  if (success === 'true') return <SuccessIcon color="success" fontSize="small" />
  if (success === 'partial') return <WarningIcon color="warning" fontSize="small" />
  return <ErrorIcon color="error" fontSize="small" />
}

export const AuditLogDrawer: React.FC<AuditLogDrawerProps> = ({
  open,
  onClose,
  log,
  labelMap,
}) => {
  const eventLabel = log
    ? (labelMap?.get(log.event_type) || humaniseEventType(log.event_type))
    : ''
  const category = log ? formatCategory(log.event_category) : ''
  const sevColor = log ? severityColor(log.severity) : undefined

  const target = log
    ? [log.resource_type, log.resource_name || log.resource_id].filter(Boolean).join(' - ')
    : ''

  const details = (log?.details && typeof log.details === 'object') ? log.details : {}
  const counts = COUNT_KEYS
    .filter(([k]) => details[k] !== undefined && details[k] !== null)
    .map(([k, label]) => {
      const v = details[k]
      const display = Array.isArray(v) ? v.length : v
      return `${display} ${label.toLowerCase()}`
    })

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: `min(${APP_DRAWER_WIDTH}px, 100vw)`,
          bgcolor: APP_DRAWER_BG,
          boxShadow: APP_DRAWER_SHADOW,
        },
      }}
    >
      {log && (
        <AppDrawerShell>
          <AppDrawerHeader
            eyebrow="AUDIT EVENT"
            title={eventLabel}
            subtitle={`${log.user_email || 'System'} - ${formatDateTime(log.created_at)}`}
            onClose={onClose}
          />
          <AppDrawerBody padding={{ p: 2 }}>
            {/* SUMMARY */}
            <Typography variant="overline" color="text.secondary">Summary</Typography>
            <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
              <SummaryRow label="Event">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  {eventLabel}
                  {category && (
                    <Typography variant="caption" color="text.secondary">({category})</Typography>
                  )}
                  {sevColor && (
                    <Chip
                      label={SEVERITY_LABELS[log.severity] || log.severity}
                      color={sevColor}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                    />
                  )}
                </Box>
              </SummaryRow>
              <SummaryRow label="When">{formatDateTime(log.created_at)}</SummaryRow>
              <SummaryRow label="User">{log.user_email || 'System'}</SummaryRow>
              {target ? <SummaryRow label="Target">{target}</SummaryRow> : null}
              {log.fp_code ? <SummaryRow label="Franchise Partner">{log.fp_code}</SummaryRow> : null}
              <SummaryRow label="Outcome">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {outcomeIcon(log.success)}
                  {resultLabel(log.success)}
                  {log.error_message ? ` - ${log.error_message}` : ''}
                </Box>
              </SummaryRow>
              {counts.length > 0 ? <SummaryRow label="Affected">{counts.join(' - ')}</SummaryRow> : null}
              {log.sub_type ? <SummaryRow label="Sub-type">{log.sub_type}</SummaryRow> : null}
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            {/* CONTEXT */}
            <Typography variant="overline" color="text.secondary">Context</Typography>
            <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
              {log.request_method ? (
                <SummaryRow label="Request">
                  {log.request_method} {log.request_path || ''}
                </SummaryRow>
              ) : null}
              {log.request_id ? <SummaryRow label="Request ID">{log.request_id}</SummaryRow> : null}
              {log.session_id ? <SummaryRow label="Session">{log.session_id}</SummaryRow> : null}
              {log.duration_formatted ? <SummaryRow label="Duration">{log.duration_formatted}</SummaryRow> : null}
              {log.ip_address ? <SummaryRow label="IP Address">{log.ip_address}</SummaryRow> : null}
              <SummaryRow label="Event ID">{log.event_id}</SummaryRow>
            </Grid>

            {/* RAW (demoted) */}
            {log.details && (
              <Accordion disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Raw details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            {(log.old_values || log.new_values) && (
              <Accordion disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Before / After</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {log.old_values && (
                    <>
                      <Typography variant="caption" color="text.secondary">Before</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'warning.light', mb: 1 }}>
                        <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.old_values, null, 2)}
                        </pre>
                      </Paper>
                    </>
                  )}
                  {log.new_values && (
                    <>
                      <Typography variant="caption" color="text.secondary">After</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                        <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(log.new_values, null, 2)}
                        </pre>
                      </Paper>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </AppDrawerBody>
        </AppDrawerShell>
      )}
    </Drawer>
  )
}

export default AuditLogDrawer
